import React, { useEffect } from 'react';
import {
  Modal,
  Typography,
  Form,
  Input,
  Button,
  PhoneNumber,
  useToast,
  Switch,
} from '@crisellevaldez/speculo-ui';
import { Organization, PrimaryContact } from '../../../types/organization.types';
import { useUpdateOrganizationSettings } from '../../../hooks/organizations/useUpdateOrganizationSettings';
import { z } from 'zod';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

interface PointOfContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg?: Organization;
  onSuccess?: () => void;
}

const pointOfContactSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  title: z.string().min(1, 'Title is required'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(10, 'Valid phone number is required'),
  useForPropertyPage: z.boolean().optional(),
});

type FormData = z.infer<typeof pointOfContactSchema>;

export const PointOfContactModal: React.FC<PointOfContactModalProps> = ({
  isOpen,
  onClose,
  selectedOrg,
  onSuccess,
}) => {
  const { updateSettings, isPending } = useUpdateOrganizationSettings();
  const { showToast } = useToast();

  const {
    control,
    handleSubmit,
    reset,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(pointOfContactSchema),
    defaultValues: {
      name: (selectedOrg?.primary_contact as PrimaryContact)?.name || '',
      title: (selectedOrg?.primary_contact as PrimaryContact)?.title || '',
      email: (selectedOrg?.primary_contact as PrimaryContact)?.email || '',
      phone: (selectedOrg?.primary_contact as PrimaryContact)?.phone || '',
      useForPropertyPage:
        (selectedOrg?.primary_contact as PrimaryContact)?.useForPropertyPage ||
        (selectedOrg?.primary_contact as PrimaryContact)?.use_for_property_page || false,
    },
  });

  useEffect(() => {
    if (isOpen && selectedOrg) {
      const pc = selectedOrg.primary_contact as PrimaryContact;
      reset({
        name: pc?.name || '',
        title: pc?.title || '',
        email: pc?.email || '',
        phone: pc?.phone || '',
        useForPropertyPage: pc?.useForPropertyPage || pc?.use_for_property_page || false,
      });
    }
  }, [isOpen, selectedOrg, reset]);

  const onSubmit = async (data: FormData) => {
    if (!selectedOrg) return;

    try {
      await updateSettings({
        id: selectedOrg.id,
        data: {
          primary_contact: {
            name: data.name,
            title: data.title,
            email: data.email,
            phone: data.phone,
            useForPropertyPage: data.useForPropertyPage,
          },
        },
      });

      showToast({
        message: 'Primary contacts successfully updated',
        variant: 'success',
      });
      onSuccess?.();
      onClose();
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : 'Failed to update primary contact',
        variant: 'error',
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header>
        <Typography variant="h3">Point of Contact</Typography>
      </Modal.Header>
      <Modal.Body>
        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-2 gap-4">
            <Form.Control required>
              <Form.Label>Name</Form.Label>
              <Input {...register('name')} placeholder="Enter name" error={errors.name?.message} />
            </Form.Control>
            <Form.Control required>
              <Form.Label>Title</Form.Label>
              <Input {...register('title')} placeholder="Enter title" error={errors.title?.message} />
            </Form.Control>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Form.Control required>
              <Form.Label>Email</Form.Label>
              <Input
                {...register('email')}
                type="email"
                placeholder="you@example.com"
                error={errors.email?.message}
              />
            </Form.Control>
            <Form.Control required>
              <Form.Label>Phone</Form.Label>
              <PhoneNumber
                value={watch('phone')}
                onChange={(val) => setValue('phone', val || '')}
                placeholder="Enter phone number"
                defaultCountry="US"
                error={errors.phone?.message}
              />
            </Form.Control>
          </div>

          <div className="mt-4">
            <div className="flex items-center gap-3">
              <Controller
                control={control}
                name="useForPropertyPage"
                render={({ field }) => (
                  <Switch
                    checked={field.value || false}
                    onChange={(checked) => field.onChange(checked)}
                    size="sm"
                  />
                )}
              />
              <Typography variant="small">Use for Property Pages</Typography>
            </div>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} type="button">
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit(onSubmit)}
          isLoading={isPending}
          disabled={isPending}
        >
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
